import React, { useCallback, useMemo } from "react";
import { Banner } from "@/components/Banner";
import { ExternalLinkedAccountCard } from "@/components/Views/LinkedAccounts";
import { IntegrationType } from "@/utils/integrationConfig";
import { IntegrationTypeConfig } from "@/utils/integrationConfig";

interface MissingIntegrationsProps {
  missingIntegrations: Array<string | string[]>;
  onSuccess?: () => void;
  save?: () => Promise<any>;
  nodeId?: string;
  canEditIntegrations: boolean;
  bannerType?: "node" | "action";
}

export const MissingIntegrations = ({
  missingIntegrations,
  onSuccess,
  save,
  nodeId = "missing",
  canEditIntegrations,
  bannerType = "node",
}: MissingIntegrationsProps) => {
  // For now we're finding the common name by finding the longest substring that is common to all the integrations in an "OR" group
  // The objective is to group all integrations that are related to the same service under the same name in the future
  // and show them in the integration modal perhaps with tabs
  const getCommonName = useCallback((integration: string | string[]) => {
    const slugs = Array.isArray(integration) ? integration : [integration];
    const names = slugs.map(
      (s) =>
        IntegrationTypeConfig[s as keyof typeof IntegrationTypeConfig]?.name ||
        s
    );
    if (names.length === 1) return names[0];

    let longest = "";
    const first = names[0];
    for (let i = 0; i < first.length; i++) {
      for (let j = i + 1; j <= first.length; j++) {
        const substring = first.substring(i, j);
        if (
          substring.length > longest.length &&
          names.every((n) => n.toLowerCase().includes(substring.toLowerCase()))
        ) {
          longest = substring;
        }
      }
    }
    return longest.trim() || names[0];
  }, []);

  const bannerLabel = useMemo(() => {
    if (missingIntegrations.length === 0) return "";
    const groupNames = missingIntegrations.map((m) => getCommonName(m));
    const entityName = bannerType === "node" ? "node" : "action";

    if (groupNames.length === 1)
      return `Connect ${groupNames[0]} to use this ${entityName}`;

    const last = groupNames.pop();
    if (groupNames.length === 0)
      return `Connect ${last} to use this ${entityName}`;

    return `Connect both ${groupNames.join(", ")} and ${last} to use this ${entityName}`;
  }, [missingIntegrations, getCommonName, bannerType]);

  if (missingIntegrations.length === 0) return null;

  return (
    <div className="mt-4">
      <Banner label={bannerLabel} />
      <div className="w-full flex flex-col items-stretch gap-6 mt-6">
        {missingIntegrations.map((integration, index) => {
          const commonName = getCommonName(integration);
          return (
            <div key={`${nodeId}-${index}`} className="flex flex-col gap-2">
              <p className="text-sm font-medium">{commonName} connection</p>
              <div className="flex flex-col gap-2">
                {Array.isArray(integration) ? (
                  integration.map((i, iIndex) => (
                    <ExternalLinkedAccountCard
                      key={`${nodeId}-${index}-${iIndex}`}
                      slug={i as IntegrationType}
                      onSuccess={onSuccess}
                      save={save}
                      isDisabled={!canEditIntegrations}
                    />
                  ))
                ) : (
                  <ExternalLinkedAccountCard
                    key={`${nodeId}-${index}`}
                    slug={integration as IntegrationType}
                    onSuccess={onSuccess}
                    save={save}
                    isDisabled={!canEditIntegrations}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
